<script setup>
/**
 * O esqueleto da lista de conversas — a forma do cartão enquanto a primeira
 * página da aba chega.
 *
 * POR QUE EXISTE (16/09/2026, relato do A&T): ao abrir "Todos", apareciam
 * primeiro as conversas de "Minhas" que já estavam na memória, soltas no topo,
 * e a roda girava embaixo delas; quando a lista de verdade chegava, tudo
 * pulava de lugar. O esqueleto tem o desenho do cartão (rosto, nome, hora,
 * prévia, uma ficha), então a chegada não mexe em nada — só preenche.
 */
import { useI18n } from 'vue-i18n';

defineProps({
  quantos: { type: Number, default: 7 },
});

const { t } = useI18n();

// Larguras diferentes por linha: nomes e prévias reais não têm o mesmo
// tamanho, e barras iguais leem como tabela.
const NOMES = ['58%', '44%', '66%', '50%', '38%', '62%', '47%'];
const PREVIAS = ['82%', '70%', '90%', '64%', '78%', '86%', '72%'];
</script>

<template>
  <div class="esc" role="status" aria-busy="true">
    <span class="sr-only">{{ t('CHAT_LIST.LOADING') }}</span>
    <div
      v-for="n in quantos"
      :key="n"
      class="esc__item"
      :style="{
        '--i': n - 1,
        '--nome': NOMES[(n - 1) % NOMES.length],
        '--previa': PREVIAS[(n - 1) % PREVIAS.length],
      }"
      aria-hidden="true"
    >
      <span class="esc__peca esc__rosto" />
      <span class="esc__corpo">
        <span class="esc__linha">
          <span class="esc__peca esc__nome" />
          <span class="esc__peca esc__hora" />
        </span>
        <span class="esc__peca esc__previa" />
        <span class="esc__peca esc__ficha" />
      </span>
    </div>
  </div>
</template>

<style scoped>
.esc {
  display: flex;
  flex-direction: column;
  padding-top: var(--esp-1);
}

/* Mesmas medidas do `.conv-item`: margem, recuo e vão. */
.esc__item {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-3);
  margin: var(--esp-1) var(--esp-2);
  padding: var(--esp-3);
  animation: escEntra var(--mov-3) var(--mov-curva) backwards;
  animation-delay: calc(var(--i) * var(--cascata-passo));
}

.esc__corpo {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-2);
  min-width: 0;
  padding-top: var(--esp-1);
}

.esc__linha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-3);
}

.esc__peca {
  position: relative;
  display: block;
  overflow: hidden;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
}

/* A luz atravessa cada peça, e cada cartão entra um passo depois do outro:
   a lista parece preencher de cima para baixo. */
.esc__peca::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgb(255 255 255 / 55%) 50%,
    transparent 100%
  );
  translate: -100% 0;
  animation: escVarre var(--mov-varredura) var(--mov-curva) infinite;
  animation-delay: calc(var(--i) * var(--cascata-passo) * 2);
}

.dark .esc__peca::after {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgb(255 255 255 / 6%) 50%,
    transparent 100%
  );
}

.esc__rosto {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.esc__nome {
  width: var(--nome);
  height: 12px;
}

.esc__hora {
  flex-shrink: 0;
  width: 34px;
  height: 9px;
}

.esc__previa {
  width: var(--previa);
  height: 10px;
}

.esc__ficha {
  width: 72px;
  height: 18px;
  opacity: 0.7;
}

@keyframes escVarre {
  to {
    translate: 200% 0;
  }
}

@keyframes escEntra {
  from {
    opacity: 0;
    translate: 0 4px;
  }
}

/* Com menos movimento, a forma fica parada — ela já diz o que vem. */
@media (prefers-reduced-motion: reduce) {
  .esc__item {
    animation: none;
  }

  .esc__peca::after {
    display: none;
  }
}
</style>
